//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import moment from 'moment-timezone';

import { getExperiencePreview } from '@apis/experiences/experiencesApi';
import * as userSettingsApi from '@apis/user-settings/userSettingsApi';
import { IGalleriesPinnedMediaDismissedAlerts } from '@apis/user-settings/userSettingsApiModel';
import { pinnedMediaToolbarActions } from '@galleries/actions/pinnedMediaToolbarActions';
import { PREVIEW_GALLERY_MODAL_ACTIONS } from '@galleries/constants/galleriesConstants';
import { PinnedMediaToolbarContext } from '@galleries/models/pinnedMediaToolbarModel';
import getSelectedGallery from '@galleries/selectors/getSelectedGallery';
import { isSelfServeGallery } from '@galleries/utils/galleryTypeUtils';
import { getEnvSpecificPreviewBaseUrlOverride } from '@galleries/utils/getEnvSpecificPreviewUrl';
import { ICuralateStore } from '@models/AppState';
import getAngularInjector from '@react-bridge/getAngularInjector';
import ExperiencesIntegrationsService from '@services/experiences-integrations-service';
import { createAction } from '@utils/actions';
import { crl8 } from 'app/curalate.globals';
import { ReduxDispatch } from 'app/curalate.store';
import gaApi from 'app/initialization/ga/ga-api';

function getExperiencesIntegrationsService(): ExperiencesIntegrationsService {
  return getAngularInjector().get('ExperiencesIntegrationsService');
}

const PINNED_MEDIA_TOOLBAR_PREVIEW_CONTEXT =
  PinnedMediaToolbarContext.PREVIEW_GALLLERY_MODAL;

const openModal = () =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.OPEN_MODAL,
  });

const closeModal = () =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.CLOSE_MODAL,
  });

const setPreviewOptions = (previewOptions: unknown) =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.SET_PREVIEW_OPTIONS,
    previewOptions,
  });

const requestDismissedAlerts = () =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.REQUEST_DISMISSED_ALERTS,
  });

const requestDismissedAlertsSucceeded = (
  alertsDismissed: IGalleriesPinnedMediaDismissedAlerts,
) =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.REQUEST_DISMISSED_ALERTS_SUCCEEDED,
    alertsDismissed,
  });

const requestDismissedAlertsFailed = () =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.REQUEST_DISMISSED_ALERTS_FAILED,
  });

const dismissAlert = (alertType: keyof IGalleriesPinnedMediaDismissedAlerts) =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.DISMISS_ALERT,
    alertType,
  });

const dismissAlertFailed = () =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.DISMISS_ALERT_FAILED,
  });

const dismissAlertSucceeded = () =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.DISMISS_ALERT_SUCCEEDED,
  });

const setAlertFirstAndLatestView = (
  alertType: keyof IGalleriesPinnedMediaDismissedAlerts,
  timestamp: number,
) =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.SET_ALERT_FIRST_AND_LATEST_VIEW,
    alertType,
    timestamp,
  });

const setAlertLatestView = (
  alertType: keyof IGalleriesPinnedMediaDismissedAlerts,
  timestamp: number,
) =>
  createAction({
    type: PREVIEW_GALLERY_MODAL_ACTIONS.SET_ALERT_LATEST_VIEW,
    alertType,
    timestamp,
  });

export type PreviewGalleryModalAction = ReturnType<
  | typeof openModal
  | typeof closeModal
  | typeof setPreviewOptions
  | typeof requestDismissedAlerts
  | typeof requestDismissedAlertsSucceeded
  | typeof requestDismissedAlertsFailed
  | typeof dismissAlert
  | typeof dismissAlertFailed
  | typeof dismissAlertSucceeded
  | typeof setAlertFirstAndLatestView
  | typeof setAlertLatestView
>;

export const previewGalleryModalActions = {
  closeModal: () => (dispatch: ReduxDispatch) => {
    dispatch(closeModal());
    dispatch(
      pinnedMediaToolbarActions.closeToolbar(
        PINNED_MEDIA_TOOLBAR_PREVIEW_CONTEXT,
      ),
    );
  },

  openModal: () => (dispatch: ReduxDispatch) => {
    gaApi.recordEvent(
      gaApi.CATEGORY_GALLERIES,
      gaApi.ACTION_GALLERIES_GALLERY_PREVIEW,
      gaApi.LABEL_TOUCHPOINTS_GALLERIES,
    );

    dispatch(openModal());
    dispatch(previewGalleryModalActions.requestAlertsDismissed());
    dispatch(
      pinnedMediaToolbarActions.openToolbar(
        PINNED_MEDIA_TOOLBAR_PREVIEW_CONTEXT,
      ),
    );
  },

  dismissAlertsAfterSevenDays: () => (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const {
      cannotPinPdpMedia,
      dragAndDropMedia,
    } = getState().galleries.previewGalleryModal.alertsDismissed;

    const dismissIfViewedSevenDaysAgo = (
      firstViewTimestamp: number,
      dismissAction: () => any,
    ) => {
      const SEVEN_DAYS_AGO = moment().subtract(7, 'days').valueOf();

      if (SEVEN_DAYS_AGO > firstViewTimestamp) {
        dispatch(dismissAction());
      }
    };

    if (cannotPinPdpMedia && !cannotPinPdpMedia?.isDismissed) {
      dismissIfViewedSevenDaysAgo(
        cannotPinPdpMedia?.firstViewTimestamp,
        previewGalleryModalActions.dismissCannotPinItemsAlert,
      );
    }

    if (dragAndDropMedia && !dragAndDropMedia?.isDismissed) {
      dismissIfViewedSevenDaysAgo(
        dragAndDropMedia?.firstViewTimestamp,
        previewGalleryModalActions.dismissRearrangeItemsAlert,
      );
    }
  },

  getPreviewOptions: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const selectedGallery = getSelectedGallery(getState());

    // TODO: Resolve how to handle previews for custom galleries
    if (selectedGallery && isSelfServeGallery(selectedGallery)) {
      const experiencePreviewOptions = {
        layout: selectedGallery.layoutType,
        source: selectedGallery.dataSourceId,
        customizationOptions: selectedGallery.customizationOptions,
        containerId: selectedGallery.containerId,
      };

      const result = await getExperiencePreview(
        selectedGallery.containerId,
        experiencePreviewOptions,
      );
      const experienceOptions = JSON.parse(result.snippet);

      const baseUrl = getEnvSpecificPreviewBaseUrlOverride();
      if (baseUrl) {
        experienceOptions.api.base = baseUrl;
      }

      dispatch(setPreviewOptions(experienceOptions));
    }
  },

  requestAlertsDismissed: () => async (dispatch: ReduxDispatch) => {
    dispatch(requestDismissedAlerts());

    try {
      const dismissedAlerts = await userSettingsApi.getGalleriesPinnedMediaDismissedAlerts();
      dispatch(requestDismissedAlertsSucceeded(dismissedAlerts));
      dispatch(previewGalleryModalActions.dismissAlertsAfterSevenDays());
    } catch (error) {
      crl8.logger?.cloud.error(error);
      dispatch(requestDismissedAlertsFailed());
    }
  },

  dismissCannotPinItemsAlert: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const {
      cannotPinPdpMedia: cannotPinpPdpMediaAlert,
    } = getState().galleries.previewGalleryModal.alertsDismissed;
    dispatch(dismissAlert('cannotPinPdpMedia'));

    try {
      await userSettingsApi.setGalleriesPinnedMediaDismissedAlerts({
        cannotPinPdpMedia: {
          ...cannotPinpPdpMediaAlert,
          isDismissed: true,
        },
      });

      dispatch(dismissAlertSucceeded());
    } catch {
      dispatch(dismissAlertFailed());
    }
  },

  dismissRearrangeItemsAlert: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const {
      dragAndDropMedia: dragAndDropMediaAlert,
    } = getState().galleries.previewGalleryModal.alertsDismissed;
    dispatch(dismissAlert('dragAndDropMedia'));

    try {
      await userSettingsApi.setGalleriesPinnedMediaDismissedAlerts({
        dragAndDropMedia: {
          ...dragAndDropMediaAlert,
          isDismissed: true,
        },
      });

      dispatch(dismissAlertSucceeded());
    } catch {
      dispatch(dismissAlertFailed());
    }
  },

  setCannotPinItemsAlertFirstView: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const {
      cannotPinPdpMedia: cannotPinpPdpMediaAlert,
    } = getState().galleries.previewGalleryModal.alertsDismissed;
    const timestamp = Date.now();

    dispatch(setAlertFirstAndLatestView('cannotPinPdpMedia', timestamp));
    await userSettingsApi.setGalleriesPinnedMediaDismissedAlerts({
      cannotPinPdpMedia: {
        ...cannotPinpPdpMediaAlert,
        firstViewTimestamp: timestamp,
        lastTimeSeen: timestamp,
      },
    });
  },

  setRearrangeItemsAlertFirstView: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const {
      dragAndDropMedia: dragAndDropAlert,
    } = getState().galleries.previewGalleryModal.alertsDismissed;
    const timestamp = Date.now();

    dispatch(setAlertFirstAndLatestView('dragAndDropMedia', timestamp));
    await userSettingsApi.setGalleriesPinnedMediaDismissedAlerts({
      dragAndDropMedia: {
        ...dragAndDropAlert,
        firstViewTimestamp: timestamp,
        lastTimeSeen: timestamp,
      },
    });
  },

  setCannotPinItemsAlertLatestView: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const {
      cannotPinPdpMedia: cannotPinpPdpMediaAlert,
    } = getState().galleries.previewGalleryModal.alertsDismissed;
    const timestamp = Date.now();

    dispatch(setAlertLatestView('cannotPinPdpMedia', timestamp));
    await userSettingsApi.setGalleriesPinnedMediaDismissedAlerts({
      cannotPinPdpMedia: {
        ...cannotPinpPdpMediaAlert,
        lastTimeSeen: timestamp,
      },
    });
  },

  setRearrangeItemsAlertLatestView: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const {
      dragAndDropMedia: dragAndDropMediaAlert,
    } = getState().galleries.previewGalleryModal.alertsDismissed;
    const timestamp = Date.now();

    dispatch(setAlertLatestView('dragAndDropMedia', timestamp));
    await userSettingsApi.setGalleriesPinnedMediaDismissedAlerts({
      dragAndDropMedia: {
        ...dragAndDropMediaAlert,
        lastTimeSeen: timestamp,
      },
    });
  },

  updatePreview: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const selectedGallery = getSelectedGallery(getState());
    const { previewOptions, isOpen } = getState().galleries.previewGalleryModal;

    // TODO: Resolve how to handle previews for custom galleries
    if (
      selectedGallery &&
      previewOptions &&
      isOpen &&
      isSelfServeGallery(selectedGallery)
    ) {
      const experiencesIntegrationService = getExperiencesIntegrationsService();
      await experiencesIntegrationService.destroyAllFrameExperiences();
      await experiencesIntegrationService.createFrameExperienceFromOptions(
        selectedGallery.layoutType,
        previewOptions,
      );
    }
  },
};
